<template>
  <div>
    <ListToolbar
      v-model:search="search"
      search-placeholder="Search lives..."
      :total="pagination.total"
      :page="pagination.page"
      :limit="pagination.limit"
      :active-filter-count="activeFilterCount"
      v-model:sort="sort"
      :sort-options="sortOptions"
      @reload="fetchLives"
      @page-change="goToPage"
      @clear-filters="clearFilters"
    >
      <template #filters>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1.5">Publish Status</label>
          <select v-model="filters.publish_status">
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1.5">Input Type</label>
          <select v-model="filters.input_type">
            <option value="">All</option>
            <option value="RTMP_PUSH">RTMP</option>
            <option value="SRT_PUSH">SRT</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1.5">Input Status</label>
          <select v-model="filters.input_status">
            <option value="">All</option>
            <option value="provisioning">Provisioning</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1.5">Channel Status</label>
          <select v-model="filters.channel_status">
            <option value="">All</option>
            <option value="awaiting">Awaiting</option>
            <option value="provisioning">Provisioning</option>
            <option value="awaiting_input">Awaiting Input</option>
            <option value="streaming">Streaming</option>
            <option value="stopped">Stopped</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1.5">Tag</label>
          <TagSelect v-model="filters.tags" placeholder="Filter by tags" :allow-create="false" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1.5">Category</label>
          <CategorySelect ref="categorySelect" v-model="filters.category_id" placeholder="All categories" preload />
        </div>
      </template>
      <template v-if="$checkPermission('livestreams.create')" #actions>
        <button
          @click="showCreateModal = true"
          class="btn-primary"
        >
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          New Live
        </button>
      </template>
    </ListToolbar>

    <!-- Table -->
    <div v-if="loading || lives.length" :class="{ 'animate-fade-in': !loading }" class="bg-white rounded-xl border border-gray-200/60 table-scroll card-shadow">
      <table class="w-full">
        <thead>
          <tr class="bg-gray-50 border-b border-gray-100">
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Name</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Input Type</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Input</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Transcoder</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Tags</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Category</th>
            <th class="text-right text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5"></th>
          </tr>
        </thead>
        <tbody v-if="loading">
          <tr v-for="i in 8" :key="'skel-' + i" class="border-b border-gray-50">
            <td class="px-6 py-4">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-lg bg-gray-100 skeleton-pulse shrink-0" />
                <div class="space-y-2 flex-1">
                  <div class="h-3.5 bg-gray-100 rounded skeleton-pulse" :style="{ width: (40 + i * 5) + '%' }" />
                  <div class="h-3 bg-gray-50 rounded skeleton-pulse w-16" />
                </div>
              </div>
            </td>
            <td class="px-6 py-4"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-20" /></td>
            <td class="px-6 py-4"><div class="h-6 bg-gray-100 rounded-full skeleton-pulse w-16" /></td>
            <td class="px-6 py-4"><div class="h-6 bg-gray-100 rounded-full skeleton-pulse w-16" /></td>
            <td class="px-6 py-4"><div class="h-6 bg-gray-100 rounded-md skeleton-pulse w-10" /></td>
            <td class="px-6 py-4"><div class="h-6 bg-gray-100 rounded-md skeleton-pulse w-10" /></td>
            <td class="px-6 py-4"><div class="h-8 bg-gray-100 rounded-lg skeleton-pulse w-8 ml-auto" /></td>
          </tr>
        </tbody>
        <tbody v-else class="divide-y divide-gray-50">
          <tr
            v-for="live in lives"
            :key="live._id"
            class="hover:bg-gray-50/50 transition-colors"
          >
            <!-- Name + status pill -->
            <td class="px-6 py-4">
              <div class="flex items-center gap-3">
                <div :class="['w-10 h-10 rounded-lg flex items-center justify-center shrink-0 relative', live.transcoding?.channel?.status === 'streaming' ? 'bg-green-50' : 'bg-gray-100']">
                  <svg class="w-4 h-4" :class="live.transcoding?.channel?.status === 'streaming' ? 'text-green-500' : 'text-gray-400'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9"/><path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.4"/><circle cx="12" cy="12" r="2" fill="currentColor"/><path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.4"/><path d="M19.1 4.9C23 8.8 23 15.2 19.1 19.1"/></svg>
                  <span v-if="live.transcoding?.channel?.status === 'streaming'" class="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-green-500 border-2 border-white animate-pulse" />
                </div>
                <div>
                  <router-link :to="{ name: 'lives-edit', params: { id: live._id } }" v-if="$checkPermission('livestreams.update') && !['provisioning', 'awaiting'].includes(live.transcoding?.channel?.status)" class="text-[14px] text-gray-900 hover:text-[var(--color-primary)] transition-colors">{{ live.meta?.name || live.name }}</router-link>
                  <span v-else class="text-[14px] text-gray-900 truncate block">{{ live.meta?.name || live.name }}</span>
                  <div class="flex items-center gap-1.5 mt-0.5">
                    <span class="inline-block w-1.5 h-1.5 rounded-full" :class="live.status === 'published' ? 'bg-green-500' : 'bg-gray-300'"></span>
                    <span class="text-sm text-gray-500 capitalize">{{ live.status || '---' }}</span>
                  </div>
                </div>
              </div>
            </td>
            <!-- Input Type -->
            <td class="px-6 py-4">
              <span class="text-sm text-gray-600">{{ inputTypeLabel(live.transcoding?.input?.type) }}</span>
            </td>
            <!-- Input Status -->
            <td class="px-6 py-4">
              <span :class="transcodingStatusClass(live.transcoding?.input?.status)">{{ live.transcoding?.input?.status || '---' }}</span>
            </td>
            <!-- Transcoder Status -->
            <td class="px-6 py-4">
              <span :class="transcodingStatusClass(live.transcoding?.channel?.status)">{{ live.transcoding?.channel?.status || '---' }}</span>
            </td>
            <!-- Tags -->
            <td class="px-6 py-4">
              <button
                v-if="live.tags && live.tags.length"
                @click="openQuickTags(live)"
                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm font-medium bg-[var(--color-primary-light)] text-[var(--color-primary)] hover:bg-[var(--color-primary-glow)] transition-colors"
              >
                <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                {{ live.tags.length }}
              </button>
              <button v-else @click="openQuickTags(live)" class="text-sm text-gray-400 hover:text-gray-500 transition-colors">+ Add</button>
            </td>
            <!-- Category -->
            <td class="px-6 py-4">
              <button
                v-if="live.categories && live.categories.length"
                @click="openQuickCategory(live)"
                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-sm font-medium bg-violet-50 text-violet-500 hover:bg-violet-100 transition-colors"
              >
                <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
                {{ live.categories.length }}
              </button>
              <button v-else @click="openQuickCategory(live)" class="text-sm text-gray-400 hover:text-gray-500 transition-colors">+ Add</button>
            </td>
            <!-- Actions -->
            <td class="px-6 py-4 text-right">
              <div class="flex items-center justify-end gap-1">
                <!-- Spinner for provisioning/awaiting -->
                <template v-if="['provisioning', 'awaiting'].includes(live.transcoding?.channel?.status)">
                  <div class="p-2">
                    <svg class="w-4 h-4 text-gray-400 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity="0.2"/><path d="M12 2a10 10 0 019.75 7.75" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>
                  </div>
                </template>
                <ActionsMenu v-else :items="liveActions(live)" />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty: no matches -->
    <div v-else-if="!loading && (search || filters.publish_status || filters.input_type || filters.input_status || filters.channel_status || filters.tags?.length || filters.category_id)" class="bg-white rounded-xl border border-gray-200/60 px-6 py-20 text-center animate-fade-in card-shadow">
      <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      </div>
      <p class="text-base font-medium text-gray-900">No matching lives found</p>
      <p class="text-sm text-gray-500 mt-1">Try adjusting your search or filters</p>
    </div>

    <!-- Empty: no records -->
    <div v-else-if="!loading" class="bg-white rounded-xl border border-gray-200/60 px-6 py-20 text-center animate-fade-in card-shadow">
      <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
        </svg>
      </div>
      <p class="text-base font-medium text-gray-900">No lives yet</p>
      <p class="text-sm text-gray-500 mt-1 mb-5">Create your first livestream to get started</p>
      <button
        @click="showCreateModal = true"
        class="btn-primary"
      >
        <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        New Live
      </button>
    </div>

    <!-- Create Modal -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showCreateModal" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div class="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col animate-fade-in">
            <div class="flex items-center justify-between px-6 pt-6 shrink-0">
              <h3 class="text-lg font-semibold text-gray-900">Create Livestream</h3>
              <button @click="showCreateModal = false" class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <form @submit.prevent="createLive" class="flex-1 flex flex-col overflow-hidden">
              <div class="flex-1 overflow-y-auto p-6 pt-5 space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1.5">Name <span class="text-red-400">*</span></label>
                <input v-model="newLive.meta.name" type="text" placeholder="e.g. Main Stage Stream" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-2">Input Type <span class="text-red-400">*</span></label>
                <div class="grid grid-cols-2 gap-3">
                  <label :class="['flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all', newLive.transcoding.input.type === 'RTMP_PUSH' ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)]' : 'border-gray-200 hover:border-gray-300']">
                    <input type="radio" name="input_type" value="RTMP_PUSH" v-model="newLive.transcoding.input.type" class="mt-0.5 accent-[var(--color-primary)]" />
                    <div>
                      <p class="text-sm font-semibold" :class="newLive.transcoding.input.type === 'RTMP_PUSH' ? 'text-[var(--color-primary)]' : 'text-gray-700'">RTMP</p>
                      <p class="text-sm text-gray-400 mt-0.5">Standard push protocol, widely supported by encoders and OBS</p>
                    </div>
                  </label>
                  <label :class="['flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all', newLive.transcoding.input.type === 'SRT_PUSH' ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)]' : 'border-gray-200 hover:border-gray-300']">
                    <input type="radio" name="input_type" value="SRT_PUSH" v-model="newLive.transcoding.input.type" class="mt-0.5 accent-[var(--color-primary)]" />
                    <div>
                      <p class="text-sm font-semibold" :class="newLive.transcoding.input.type === 'SRT_PUSH' ? 'text-[var(--color-primary)]' : 'text-gray-700'">SRT</p>
                      <p class="text-sm text-gray-400 mt-0.5">Low-latency secure transport, ideal for unreliable networks</p>
                    </div>
                  </label>
                </div>
              </div>

              </div>
              <div class="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 shrink-0">
                <button type="button" @click="showCreateModal = false" class="btn-secondary">Cancel</button>
                <button type="submit" :disabled="creating" class="btn-primary">
                  {{ creating ? 'Creating...' : 'Create' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Hidden lookups (always mounted for name resolution) -->
    <CategorySelect ref="categoryLookup" class="hidden" preload />
    <TagSelect ref="tagLookup" class="hidden" preload />

    <!-- Quick edit tags -->
    <QuickEditModal :show="showQuickTags" title="Edit Tags" :saving="quickSaving" @close="showQuickTags = false" @save="saveQuickTags">
      <TagSelect v-model="quickTagIds" />
    </QuickEditModal>

    <!-- Quick edit category -->
    <QuickEditModal :show="showQuickCategory" title="Edit Categories" :saving="quickSaving" @close="showQuickCategory = false" @save="saveQuickCategory">
      <CategorySelect v-model="quickCategoryIds" />
    </QuickEditModal>

    <!-- Video player modal -->
    <MediaPlayerModal
      :show="!!videoUrl"
      title="Live Stream"
      :hls-url="videoUrl || ''"
      :formats="[]"
      :live="true"
      @close="videoUrl = null"
    />

    <!-- Status history modal (admin only) -->
    <StatusHistoryModal
      :show="!!historyLive"
      :livestream-id="historyLive?._id"
      :subtitle="historyLive?.meta?.name || historyLive?.name"
      @close="historyLive = null"
    />

    <!-- Delete confirm -->
    <ConfirmModal
      :show="showDeleteModal" :loading="deleting"
      title="Delete Live"
      :message="`Are you sure you want to delete '${liveToDelete?.meta?.name || liveToDelete?.name}'? This action cannot be undone.`"
      confirm-text="Delete"
      :danger="true"
      @confirm="deleteLive"
      @cancel="showDeleteModal = false"
    />
  </div>
</template>

<script>
import ListToolbar from '../../components/ListToolbar.vue'
import ActionsMenu from '../../components/ActionsMenu.vue'
import icons from '../../components/action-icons.js'
import ConfirmModal from '../../components/ConfirmModal.vue'
import MediaPlayerModal from '../../components/MediaPlayerModal.vue'
import QuickEditModal from '../../components/QuickEditModal.vue'
import StatusHistoryModal from '../../components/StatusHistoryModal.vue'
import TagSelect from '../../components/TagSelect.vue'
import CategorySelect from '../../components/CategorySelect.vue'
import api from '../../services/api.js'
import { notifySuccess, notifyError } from '../../services/notifications.js'
import websocket from '../../services/websocket.js'
import livestreamDefinition from './live-definition.js'

export default {
  name: 'LivesView',
  components: { ListToolbar, ActionsMenu, ConfirmModal, MediaPlayerModal, QuickEditModal, StatusHistoryModal, TagSelect, CategorySelect },

  data() {
    return {
      lives: [],
      search: '',
      filters: {
        publish_status: '',
        input_type: '',
        input_status: '',
        channel_status: '',
        tags: [],
        category_id: null
      },
      sort: 'created:-1',
      sortOptions: [
        { value: 'name:1', label: 'Name A-Z' },
        { value: 'name:-1', label: 'Name Z-A' },
        { value: 'updated:1', label: 'Last Updated (oldest)' },
        { value: 'updated:-1', label: 'Last Updated (newest)' },
        { value: 'created:1', label: 'Created (oldest)' },
        { value: 'created:-1', label: 'Created (newest)' }
      ],
      pagination: { limit: 25, page: 1, total: 0 },
      loading: true,
      showCreateModal: false,
      showDeleteModal: false, deleting: false,
      creating: false,
      liveToDelete: null,
      newLive: livestreamDefinition(),
      videoUrl: null,
      historyLive: null,
      pollInterval: null,
      // Quick edit
      quickEditItem: null,
      quickTagIds: [],
      quickCategoryIds: [],
      showQuickTags: false,
      showQuickCategory: false,
      quickSaving: false
    }
  },

  computed: {
    isAdmin() { return this.$store.getters['auth/isAdmin'] },
    activeFilterCount() {
      return Object.values(this.filters).filter(v => Array.isArray(v) ? v.length > 0 : v !== '' && v != null).length
    }
  },

  watch: {
    search() { this.pagination.page = 1; this.fetchLives() },
    sort() { this.pagination.page = 1; this.fetchLives() },
    filters: { deep: true, handler() { this.pagination.page = 1; this.fetchLives() } }
  },

  async mounted() {
    await this.fetchLives()
    this.pollInterval = setInterval(() => this.fetchLives(true, false), 10000)
    websocket.init()
    websocket.on('livestream:update', this.handleLivestreamUpdate)
  },

  beforeUnmount() {
    if (this.pollInterval) clearInterval(this.pollInterval)
    websocket.off('livestream:update', this.handleLivestreamUpdate)
  },

  methods: {
    clearFilters() {
      this.filters = { publish_status: '', input_type: '', input_tier: '', input_status: '', channel_status: '', tags: [], category_id: null }
    },

    async fetchLives(silent = false, count = true) {
      try {
        const params = {
          limit: this.pagination.limit,
          page: this.pagination.page
        }
        if (this.search) params.text = this.search
        if (this.sort) params.sort = this.sort
        if (this.filters.publish_status) params.status = this.filters.publish_status
        if (this.filters.input_type) params.input_type = this.filters.input_type
        if (this.filters.input_status) params.input_status = this.filters.input_status
        if (this.filters.channel_status) params.channel_status = this.filters.channel_status
        if (this.filters.tags?.length) params.tags = this.filters.tags.join(',')
        if (this.filters.category_id) params.categories = this.filters.category_id
        if (count) params.count = true

        const opts = silent ? { params, silent: true } : { params }
        const res = await api.get('/livestreams', opts)
        this.lives = res.data?.items || []
        if (Number.isInteger(res.data?.total)) this.pagination.total = res.data.total
      } catch {
        // keep existing data on failure
      } finally {
        this.loading = false
      }
    },

    goToPage(page) {
      this.pagination.page = page
      this.fetchLives(false, false)
    },

    transcodingStatusClass(status) {
      if (!status) return 'text-sm text-gray-500'
      const s = status.toLowerCase()
      if (['streaming', 'success', 'running'].includes(s)) return 'badge-live'
      if (['failed', 'error'].includes(s)) return 'badge-error'
      if (['provisioning', 'awaiting', 'awaiting_input', 'starting'].includes(s)) return 'badge-warning'
      if (['stopped', 'idle'].includes(s)) return 'badge-idle'
      return 'badge-idle'
    },

    inputTypeLabel(type) {
      const labels = { 'RTMP_PUSH': 'RTMP', 'SRT_PUSH': 'SRT' }
      return labels[type] || type || '---'
    },

    getTagsList(tags) {
      if (!tags?.length) return []
      return tags.map(t => {
        if (typeof t === 'object' && t.name) return t
        const id = typeof t === 'object' ? t._id?.toString() : t?.toString()
        const name = this.$refs.tagLookup?.getTagName(id) || id
        return { _id: id, name }
      })
    },

    getCategoryName(id) {
      if (!id) return ''
      return this.$refs.categoryLookup?.getCategoryName(id) || ''
    },

    openQuickTags(live) {
      this.quickEditItem = live
      this.quickTagIds = (live.tags || []).map(t => typeof t === 'string' ? t : t._id?.toString())
      this.showQuickTags = true
    },

    openQuickCategory(live) {
      this.quickEditItem = live
      this.quickCategoryIds = (live.categories || []).map(c => typeof c === 'string' ? c : c._id?.toString())
      this.showQuickCategory = true
    },

    async saveQuickTags() {
      if (!this.quickEditItem) return
      this.quickSaving = true
      try {
        await api.put(`/livestreams/${this.quickEditItem._id}`, { livestream: { ...this.quickEditItem, tags: this.quickTagIds } })
        notifySuccess('Tags updated')
        this.showQuickTags = false
        this.fetchLives()
      } catch { /* toast */ } finally { this.quickSaving = false }
    },

    async saveQuickCategory() {
      if (!this.quickEditItem) return
      this.quickSaving = true
      try {
        await api.put(`/livestreams/${this.quickEditItem._id}`, { livestream: { ...this.quickEditItem, categories: this.quickCategoryIds } })
        notifySuccess('Categories updated')
        this.showQuickCategory = false
        this.fetchLives()
      } catch { /* toast */ } finally { this.quickSaving = false }
    },

    handleLivestreamUpdate(data) {
      const id = data._id?.toString()
      if (!id) return
      const idx = this.lives.findIndex(item => item._id?.toString() === id)
      if (idx < 0) return
      const item = this.lives[idx]
      this.lives.splice(idx, 1, {
        ...item,
        status: data.status?.livestream ?? item.status,
        is_running: data.status?.is_running ?? item.is_running,
        transcoding: {
          ...item.transcoding,
          input: {
            ...item.transcoding?.input,
            status: data.status?.input ?? item.transcoding?.input?.status
          },
          channel: {
            ...item.transcoding?.channel,
            status: data.status?.channel ?? item.transcoding?.channel?.status
          },
          recording: data.status?.recording ?? item.transcoding?.recording
        }
      })
    },

    async createLive() {
      if (!this.newLive.meta?.name?.trim()) { notifyError('Name is required'); return }
      if (!this.newLive.transcoding?.input?.type) { notifyError('Input Type is required'); return }
      this.creating = true
      try {
        await api.post('/livestreams', { livestream: this.newLive })
        notifySuccess('Livestream created')
        this.showCreateModal = false
        this.newLive = livestreamDefinition()
        this.fetchLives()
      } catch (err) {
        notifyError(err?.message || 'Error creating livestream')
      } finally { this.creating = false }
    },

    liveActions(live) {
      const streaming = live.transcoding?.channel?.status === 'streaming'
      return [
        {
          label: 'Edit',
          show: this.$checkPermission('livestreams.update'),
          to: { name: 'lives-edit', params: { id: live._id } },
          icon: icons.edit
        },
        {
          label: 'Watch',
          show: streaming,
          action: () => { this.videoUrl = this.$mediaUrl.live(live._id) },
          icon: icons.watch
        },
        {
          label: 'Editor',
          show: streaming,
          to: { name: 'editor', query: { lId: live._id } },
          icon: icons.editor
        },
        { divider: true },
        {
          label: 'Analytics',
          to: { name: 'analytics-lives', query: { id: live._id } },
          icon: icons.analytics
        },
        {
          label: 'Status History',
          show: true,
          action: () => { this.historyLive = live },
          icon: icons.history
        },
        { divider: true },
        {
          label: 'Delete',
          show: this.$checkPermission('livestreams.delete'),
          danger: true,
          action: () => this.confirmDelete(live),
          icon: icons.delete
        }
      ]
    },

    confirmDelete(live) {
      this.liveToDelete = live
      this.showDeleteModal = true
    },

    async deleteLive() {
      if (!this.liveToDelete) return
      this.deleting = true
      try {
        await api.delete(`/livestreams/${this.liveToDelete._id}`)
        notifySuccess('Live deleted')
        this.showDeleteModal = false
        this.liveToDelete = null
        this.fetchLives()
      } catch { /* toast */ } finally { this.deleting = false }
    }
  }
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
